/**
 * Orthanc REST API wrapper for efficient batch operations
 * Provides methods for fetching changes, series, instances, and lookups
 */

/**
 * Get the Orthanc server address
 * @returns {string} The Orthanc server URL
 */
export const getOrthancAddress = () => {
  return process.env.ORTHANC_URL || 'https://orthanc.azmed.co';
};

/**
 * Get the Orthanc authorization header
 * @returns {object} The authorization header
 */
const getOrthancAuthHeader = () => {
  if (typeof process !== 'undefined' && process.env) {
    return {
      Authorization: `Basic ${btoa(
        `${process.env.ORTHANC_USERNAME}:${process.env.ORTHANC_PASSWORD}`
      )}`,
    };
  }
  return {};
};

/**
 * Fetch changes from Orthanc change tracking
 * @param {number} since - Last sequence number (0 for all changes)
 * @param {number} limit - Maximum number of changes to fetch (default: 100)
 * @returns {Promise<object>} Changes data with format: { Changes: [...], Done: boolean, Last: number }
 */
export const fetchChanges = async (since = 0, limit = 100) => {
  try {
    const url = `${getOrthancAddress()}/changes?since=${since}&limit=${limit}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getOrthancAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch changes: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('[orthancAPI] Error fetching changes:', error);
    throw error;
  }
};

/**
 * Fetch all series from Orthanc in a single batch call
 * @param {boolean} expand - Whether to expand series details (default: true)
 * @returns {Promise<Array>} Array of series objects
 */
export const fetchAllSeries = async (expand = true) => {
  try {
    const url = `${getOrthancAddress()}/series${expand ? '?expand' : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getOrthancAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch series: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[orthancAPI] Error fetching all series:', error);
    throw error;
  }
};

/**
 * Fetch all instances from Orthanc in a single batch call
 * @param {boolean} expand - Whether to expand instance details (default: true)
 * @returns {Promise<Array>} Array of instance objects
 */
export const fetchAllInstances = async (expand = true) => {
  try {
    const url = `${getOrthancAddress()}/instances${expand ? '?expand' : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getOrthancAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch instances: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[orthancAPI] Error fetching all instances:', error);
    throw error;
  }
};

/**
 * Lookup Orthanc internal ID from DICOM UID using /tools/lookup
 * @param {string} dicomUID - The DICOM UID to lookup (Study/Series/Instance UID)
 * @returns {Promise<string|null>} The Orthanc internal ID or null if not found
 */
export const lookupOrthancID = async dicomUID => {
  try {
    const url = `${getOrthancAddress()}/tools/lookup`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        ...getOrthancAuthHeader(),
      },
      body: dicomUID,
    });

    if (!response.ok) {
      console.warn(
        `[orthancAPI] Lookup failed for ${dicomUID}: ${response.status}`
      );
      return null;
    }

    const data = await response.json();
    if (data && data.length > 0 && data[0].ID) {
      return data[0].ID;
    }
    return null;
  } catch (error) {
    console.error('[orthancAPI] Error looking up Orthanc ID:', error);
    return null;
  }
};

/**
 * Fetch study metadata from Orthanc REST API
 * @param {string} studyOrthancId - The Orthanc internal study ID
 * @returns {Promise<object>} Study metadata
 */
export const fetchStudyMetadata = async studyOrthancId => {
  try {
    const url = `${getOrthancAddress()}/studies/${studyOrthancId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getOrthancAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch study metadata: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[orthancAPI] Error fetching study metadata:', error);
    throw error;
  }
};

/**
 * Fetch series for a specific study
 * @param {string} studyOrthancId - The Orthanc internal study ID
 * @returns {Promise<Array>} Array of series IDs
 */
export const fetchStudySeries = async studyOrthancId => {
  try {
    const url = `${getOrthancAddress()}/studies/${studyOrthancId}/series`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getOrthancAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch study series: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[orthancAPI] Error fetching study series:', error);
    throw error;
  }
};

/**
 * Fetch a user-defined attachment of a series, as the raw Response.
 *
 * The caller gets every status, including 304 and 404, because a caching layer
 * needs the validator headers and the status rather than a parsed body. Only a
 * network failure rejects.
 *
 * @param {string} seriesOrthancId - The Orthanc internal series ID
 * @param {number} contentType - The user content type, 1024 or above
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Aborts the request
 * @param {string} [options.ifNoneMatch] - An ETag, sent verbatim, quotes included
 * @returns {Promise<Response>} The untouched response
 */
export const fetchSeriesAttachmentResponse = async (
  seriesOrthancId,
  contentType,
  { signal, ifNoneMatch } = {}
) => {
  // The content lives under /data. Without it Orthanc answers 200 with the list
  // of sub-resources, which would parse fine and be the wrong thing entirely.
  const url = `${getOrthancAddress()}/series/${seriesOrthancId}/attachments/${contentType}/data`;
  const headers = getOrthancAuthHeader();

  if (ifNoneMatch) headers['If-None-Match'] = ifNoneMatch;

  return fetch(url, {
    method: 'GET',
    headers,
    signal,
    // Otherwise the browser's own HTTP cache can answer a cold request, which
    // would hide the revalidation entirely and never exercise the 304 path.
    cache: 'no-store',
  });
};

/**
 * Fetch the md5 of a series attachment. Used to revalidate where the ETag header
 * is unreadable, which is the case cross-origin unless the server exposes it:
 * a response body always comes through.
 * @param {string} seriesOrthancId - The Orthanc internal series ID
 * @param {number} contentType - The user content type, 1024 or above
 * @param {AbortSignal} [signal] - Aborts the request
 * @returns {Promise<string|null>} The md5, null when the attachment is absent
 */
export const fetchSeriesAttachmentMd5 = async (
  seriesOrthancId,
  contentType,
  signal
) => {
  const url = `${getOrthancAddress()}/series/${seriesOrthancId}/attachments/${contentType}/md5`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getOrthancAuthHeader(),
    signal,
    cache: 'no-store',
  });

  if (response.status === 404) return null;

  if (!response.ok) {
    throw new Error(`Failed to fetch attachment md5: ${response.status}`);
  }

  // Orthanc may quote it, like the ETag it is derived from.
  return (await response.text()).trim().replace(/^"|"$/g, '');
};

/**
 * Check if Orthanc REST API is available
 * @returns {Promise<boolean>} True if API is available
 */
export const checkOrthancAvailability = async () => {
  try {
    const url = `${getOrthancAddress()}/system`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getOrthancAuthHeader(),
      },
      timeout: 5000, // 5 second timeout
    });

    return response.ok;
  } catch (error) {
    console.error('[orthancAPI] Orthanc not available:', error);
    return false;
  }
};

export const fetchAllStudies = async () => {
  try {
    const url = `${getOrthancAddress()}/studies?expand&&requestedTags=ModalitiesInStudy;ProtocolName`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getOrthancAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch studies: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[orthancAPI] Error fetching all studies:', error);
    throw error;
  }
};

/**
 * Get study statistics (includes size information)
 * @param {string} studyInstanceUID - Study Instance UID
 * @returns {Promise<object>} Study statistics
 */
export const getStudyStatistics = async studyInstanceUID => {
  try {
    const orthancId = await lookupOrthancID(studyInstanceUID);
    if (!orthancId) {
      console.warn(
        `[orthancAPI] Could not find Orthanc ID for study ${studyInstanceUID}`
      );
      return null;
    }

    const url = `${getOrthancAddress()}/studies/${orthancId}/statistics`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getOrthancAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch statistics: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(
      `[orthancAPI] Error getting statistics for study ${studyInstanceUID}:`,
      error
    );
    return null;
  }
};

/**
 * Download study as ZIP archive
 * @param {string} studyInstanceUID - Study Instance UID
 * @param {function} onProgress - Optional progress callback (bytesLoaded, bytesTotal)
 * @returns {Promise<object>} { blob, size }
 */
export const downloadStudyArchive = async (studyInstanceUID, onProgress) => {
  try {
    // Get Orthanc internal ID
    const orthancId = await lookupOrthancID(studyInstanceUID);
    if (!orthancId) {
      throw new Error(
        `Could not find Orthanc ID for study ${studyInstanceUID}`
      );
    }

    // Download archive
    const url = `${getOrthancAddress()}/studies/${orthancId}/archive`;
    const response = await fetch(url, {
      method: 'GET',
      headers: getOrthancAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to download archive: ${response.status} ${response.statusText}`
      );
    }

    // Get content length
    const contentLength = parseInt(
      response.headers.get('Content-Length') || '0',
      10
    );

    // Read response as blob with progress tracking
    const reader = response.body.getReader();
    const chunks = [];
    let receivedLength = 0;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      receivedLength += value.length;

      // Call progress callback if provided
      if (onProgress) {
        onProgress(receivedLength, contentLength);
      }
    }

    // Combine chunks into single blob
    const blob = new Blob(chunks);

    return {
      blob,
      size: blob.size,
    };
  } catch (error) {
    console.error(
      `[orthancAPI] Error downloading archive for study ${studyInstanceUID}:`,
      error
    );
    throw error;
  }
};

export default {
  getOrthancAddress,
  fetchChanges,
  fetchAllSeries,
  fetchAllInstances,
  lookupOrthancID,
  fetchStudyMetadata,
  fetchStudySeries,
  fetchSeriesAttachmentResponse,
  fetchSeriesAttachmentMd5,
  checkOrthancAvailability,
  fetchAllStudies,
  getStudyStatistics,
  downloadStudyArchive,
};
